import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import { fetchBranches, fetchRooms, fetchSubjects } from "@/api/branches";
import { fetchTeachers } from "@/api/teachers";
import { createGroup, updateGroup } from "@/api/groups";
import { extractErrorMessage } from "@/types/common";
import { DAYSET_DAYS, DAYSET_LABELS, daysToDayset } from "@/types/enums";
import type { Dayset } from "@/types/enums";
import type { Group, GroupCreateInput } from "@/types/group";

export const TIME_SLOTS = [
  "07:30–09:00",
  "09:00–10:30",
  "10:30–12:00",
  "13:30–15:00",
  "15:00–16:30",
  "16:30–18:00",
  "18:00–19:30",
  "19:30–21:00",
];

export function GroupFormDialog({
  open,
  onClose,
  group,
  prefill,
}: {
  open: boolean;
  onClose: () => void;
  group?: Group;
  /** Предзаполнение при создании из ячейки расписания (ТЗ §2.4, прототип editGroup(null,{room,slot,dayset,branch})). */
  prefill?: { branchId?: string; roomId?: string; timeSlot?: string; dayset?: Dayset };
}) {
  const queryClient = useQueryClient();
  const isEdit = Boolean(group);
  const [serverError, setServerError] = useState<string | null>(null);

  const [code, setCode] = useState("");
  const [branchId, setBranchId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [level, setLevel] = useState("");
  const [teacherId, setTeacherId] = useState("");
  const [roomId, setRoomId] = useState("");
  const [dayset, setDayset] = useState<Dayset>("MON_WED_FRI");
  const [timeSlot, setTimeSlot] = useState(TIME_SLOTS[0]);
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    if (open) {
      setServerError(null);
      setCode(group?.code ?? "");
      setBranchId(group?.branchId ?? prefill?.branchId ?? "");
      setSubjectId(group?.subjectId ?? "");
      setLevel(group?.level ?? "");
      setTeacherId(group?.teacherId ?? "");
      setRoomId(group?.roomId ?? prefill?.roomId ?? "");
      setDayset(group ? daysToDayset(group.days) ?? "MON_WED_FRI" : prefill?.dayset ?? "MON_WED_FRI");
      setTimeSlot(group?.timeSlot ?? prefill?.timeSlot ?? TIME_SLOTS[0]);
      setStartDate(group?.startDate?.slice(0, 10) ?? new Date().toISOString().slice(0, 10));
    }
  }, [open, group, prefill]);

  const { data: branches } = useQuery({ queryKey: ["branches"], queryFn: fetchBranches });
  const { data: subjects } = useQuery({ queryKey: ["subjects"], queryFn: fetchSubjects });
  const { data: rooms } = useQuery({
    queryKey: ["rooms", branchId],
    queryFn: () => fetchRooms(branchId),
    enabled: Boolean(branchId),
  });
  const { data: teachers } = useQuery({
    queryKey: ["teachers", "for-group", branchId],
    queryFn: () => fetchTeachers({ branchId: branchId || undefined, status: "ACTIVE", limit: 200 }),
  });

  const selectedSubject = (subjects ?? []).find((s) => s.id === subjectId);

  const mutation = useMutation({
    mutationFn: () => {
      const payload: GroupCreateInput = {
        code,
        branchId,
        subjectId,
        level,
        teacherId: teacherId || null,
        roomId,
        days: DAYSET_DAYS[dayset],
        timeSlot,
        startDate,
      };
      return isEdit && group ? updateGroup(group.id, payload) : createGroup(payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["groups"] });
      onClose();
    },
    onError: (error) => {
      // Конфликт расписания (ТЗ §2.3/§7.1) — backend возвращает 409, показываем понятное сообщение.
      setServerError(extractErrorMessage(error, "Не удалось сохранить группу."));
    },
  });

  const isConflict = (mutation.error as { response?: { status?: number } } | undefined)?.response?.status === 409;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Редактирование группы" : "Новая группа"}</DialogTitle>
      <DialogContent>
        {serverError && (
          <Alert severity={isConflict ? "warning" : "error"} sx={{ mb: 2 }}>
            {isConflict
              ? `Конфликт расписания: кабинет уже занят в это время в выбранные дни. ${serverError}`
              : serverError}
          </Alert>
        )}
        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={6}>
            <TextField label="Код группы" fullWidth value={code} onChange={(e) => setCode(e.target.value)} />
          </Grid>
          <Grid item xs={6}>
            <TextField select label="Филиал" fullWidth value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              {(branches ?? []).map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              select
              label="Предмет"
              fullWidth
              value={subjectId}
              onChange={(e) => {
                setSubjectId(e.target.value);
                setLevel("");
              }}
            >
              {(subjects ?? []).map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField select label="Уровень" fullWidth value={level} onChange={(e) => setLevel(e.target.value)} disabled={!selectedSubject}>
              {(selectedSubject?.levels ?? []).map((lvl) => (
                <MenuItem key={lvl} value={lvl}>
                  {lvl}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField select label="Преподаватель" fullWidth value={teacherId} onChange={(e) => setTeacherId(e.target.value)}>
              <MenuItem value="">Не назначен</MenuItem>
              {(teachers?.items ?? []).map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.fullName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField select label="Кабинет" fullWidth value={roomId} onChange={(e) => setRoomId(e.target.value)} disabled={!branchId}>
              {(rooms ?? []).map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name} (до {r.capacity} чел.)
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField select label="Дни недели" fullWidth value={dayset} onChange={(e) => setDayset(e.target.value as Dayset)}>
              {Object.entries(DAYSET_LABELS).map(([key, lbl]) => (
                <MenuItem key={key} value={key}>
                  {lbl}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField select label="Время" fullWidth value={timeSlot} onChange={(e) => setTimeSlot(e.target.value)}>
              {TIME_SLOTS.map((slot) => (
                <MenuItem key={slot} value={slot}>
                  {slot}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Дата старта"
              type="date"
              fullWidth
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Отмена
        </Button>
        <Button
          variant="contained"
          disabled={!code || !branchId || !subjectId || !level || !roomId || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {isEdit ? "Сохранить" : "Создать"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
